import { useMemo } from 'react';
import type { CatalogVerbs } from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.types';
import { skipToken } from '@reduxjs/toolkit/query';

// Direct store-module import (not the feature barrel): the module injects its
// endpoints into baseApi at load, which is exactly what we need.
import type { TemplateCatalogBootstrapRow } from '../fixtures/types';
import {
  useBatchArchiveTemplatesMutation,
  useGetTemplateCatalogBootstrapQuery,
} from '../store/catalogApi';

export interface TemplateCatalogData {
  templates: TemplateCatalogBootstrapRow[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /**
   * Archive/restore and de-adopt/re-adopt over POST …/batch-archive, then an
   * awaited refetch of the bootstrap read so rows are fresh when the verb
   * resolves. The generic catalog calls archive(ids)/restore(ids) with row ids;
   * we split them by each row's `action` — template rows (`archive`) go to the
   * template id lists, public-adoption rows (`deadopt`) to the adoption-key
   * lists. Rejects on failure; ResourceCatalog handles the error affordance.
   */
  verbs: CatalogVerbs;
}

export const useTemplateCatalogData = (
  orgId: string | undefined
): TemplateCatalogData => {
  const query = useGetTemplateCatalogBootstrapQuery(
    orgId ? { orgId } : skipToken
  );
  const [batchArchive] = useBatchArchiveTemplatesMutation();

  // Memoize so the array reference is stable across renders (RTK returns a
  // stable `data.rows` per cache entry); otherwise the `verbs` useMemo below —
  // and the returned `templates` — would churn every render.
  const rows = useMemo(() => query.data?.rows ?? [], [query.data?.rows]);
  const { refetch } = query;

  const verbs = useMemo<CatalogVerbs>(() => {
    const actionByRowId = new Map(rows.map((row) => [row.row_id, row.action]));

    const run = async (
      ids: string[],
      direction: 'archive' | 'restore'
    ): Promise<void> => {
      if (!orgId) throw new Error('No organization in scope');
      const templateIds = ids.filter(
        (id) => actionByRowId.get(id) !== 'deadopt'
      );
      const adoptionKeys = ids.filter(
        (id) => actionByRowId.get(id) === 'deadopt'
      );
      await batchArchive({
        orgId,
        noteTemplateBatchArchiveRequest: {
          archive_template_ids: direction === 'archive' ? templateIds : [],
          restore_template_ids: direction === 'restore' ? templateIds : [],
          deadopt_keys: direction === 'archive' ? adoptionKeys : [],
          readopt_keys: direction === 'restore' ? adoptionKeys : [],
        },
      }).unwrap();
      await refetch();
    };

    return {
      archive: (ids): Promise<void> => run(ids, 'archive'),
      restore: (ids): Promise<void> => run(ids, 'restore'),
    };
  }, [orgId, batchArchive, refetch, rows]);

  return {
    templates: rows,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: (): void => {
      void query.refetch();
    },
    verbs,
  };
};
